import { AttachmentType } from "@Models/chatBot/AttachmentType";
import { BotAnswerFeedbackData } from "@Models/chatBot/BotAnswerFeedbackData";
import { ProductBotChannelData } from "@Models/chatBot/ProductBotChannelData";
import { Activity, Attachment, Message } from "botframework-directlinejs";

export function getMessageId(activity: Activity): string | undefined {
    return activity?.channelData?.MessageId;
}

export function getQnaNumber(activity: Activity): number | undefined {
    return activity?.channelData?.QnaNumber;
}

export function getKnowledgebaseId(activity: Activity): number | undefined {
    return activity?.channelData?.DboKnowledgebaseId;
}

export function getShouldShowFeedbackButtons(activity: Activity): boolean {
    return activity?.channelData?.ShouldShowFeedbackButtons ?? false;
}

export function getProductBotChannelData(
    activity: Activity
): ProductBotChannelData {
    return activity?.channelData?.ProductBotAnswer ?? null;
}

export function getBotAnswerFeedbackData(
    activity: Activity
): BotAnswerFeedbackData {
    return {
        shouldShowFeedbackButtons: getShouldShowFeedbackButtons(activity),
        messageId: getMessageId(activity) ?? "",
        qnaNumber: getQnaNumber(activity),
        knowledgeBaseId: getKnowledgebaseId(activity),
        productBotChannelData: getProductBotChannelData(activity),
    };
}

export function getShouldOfferMoreAssistance(activity: Activity): boolean {
    return activity?.channelData?.ShouldOfferMoreAssistance ?? false;
}

export function getIsValidationRetryPrompt(activity: Activity): boolean {
    return activity?.channelData?.IsValidationRetryPrompt ?? false;
}

export function getDate(activity: Activity): Date {
    return activity?.timestamp &&
        (typeof activity.timestamp === "string" ||
            (activity.timestamp as unknown) instanceof String ||
            typeof activity.timestamp === "number" ||
            (activity.timestamp as unknown) instanceof Number)
        ? new Date(activity.timestamp)
        : new Date();
}

/**
 * Checks if the given activity is a Hero Card message.
 * @param activity - The activity to check.
 * @returns True if the activity contains a Hero Card attachment, otherwise false.
 */
export function isHeroCard(activity: Activity): boolean {
    return (
        (activity.type === "message" &&
            activity.attachments?.some(
                (a: Attachment) => a.contentType === AttachmentType.HeroCard
            )) ??
        false
    );
}

/**
 * Filters out non-message activities and empty messages from the given activity list.
 * @param activities - The list of activities to filter.
 * @returns A filtered list of message activities that contain either text or attachments.
 */
export function getFilteredMessageActivities(activities: Message[]): Message[] {
    return activities.filter(({ type, text, attachments }: Message) => {
        return type === "message" && (text || (attachments?.length ?? 0) > 0);
    });
}

/**
 * Determines whether the given activity is the first consecutive message
 * after a non-bot message.
 * @param activity - The activity to check.
 * @param activities - The list of activities to reference.
 * @returns True if the activity is the first consecutive message, otherwise false.
 */
export function isFirstConsecutiveMessage(
    activity: Activity,
    activities: Activity[]
): boolean {
    const activityIndex = activities.findIndex(
        ({ id }: Activity) => id === activity.id
    );
    return (
        activityIndex > 0 && activities[activityIndex - 1].from.role !== "bot"
    );
}

/**
 * Determines whether the given activity is the last message in the filtered list.
 * @param activity - The activity to check.
 * @param filteredMessageActivities - The list of filtered message activities.
 * @returns True if the activity is the last message, otherwise false.
 */
export function isLastMessage(
    activity: Activity,
    filteredMessageActivities: Message[]
): boolean {
    return filteredMessageActivities.at(-1)?.id === activity.id;
}

/**
 * Determines whether the given activity is the last consecutive message
 * from the bot before a non-bot message or the end of the list.
 * @param activity - The activity to check.
 * @param activities - The list of activities to reference.
 * @returns True if the activity is the last consecutive message from the bot, otherwise false.
 */
export function isLastConsecutiveMessage(
    activity: Activity,
    activities: Activity[]
): boolean {
    const activityIndex = activities.findIndex(({ id }) => id === activity.id);

    if (activityIndex === -1 || activity.from.role !== "bot") {
        return false;
    }

    const nextActivity = activities[activityIndex + 1];

    return !nextActivity || nextActivity.from.role !== "bot";
}
